import {
  Alert,
  Box,
  Button,
  Divider,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import type { Tenant, TenantInvitation, TenantMember, TenantRole } from "../../api/client";
import {
  cancelTenantInvitation,
  inviteTenantMember,
  listTenantInvitations,
  listTenantMembers,
  removeTenantMember,
  updateTenantMemberRole,
} from "../../api/client";
import { useHierarchyContext } from "../../contexts/HierarchyContext";
import { CompGrid, type Column } from "../../components/grid/CompGrid";
import { Panel } from "../../components/layout/Panel";
import { useAuth } from "../../contexts/AuthContext";

const STORAGE_KEY = "openmon.settings.tenantSlug";

const ROLES: TenantRole[] = ["owner", "admin", "member", "viewer"];

function readStoredTenant(): string | null {
  const v = localStorage.getItem(STORAGE_KEY);
  return v && v.trim().length > 0 ? v : null;
}

function writeStoredTenant(slug: string | null) {
  if (!slug) {
    localStorage.removeItem(STORAGE_KEY);
  } else {
    localStorage.setItem(STORAGE_KEY, slug);
  }
}

export interface TenantMembersSettingsProps {
  /** When provided, use this tenant and hide the tenant selector (e.g. from PageTenantSettings) */
  tenantSlug?: string | null;
}

export function TenantMembersSettings({ tenantSlug: tenantSlugProp }: TenantMembersSettingsProps = {}) {
  const auth = useAuth();
  const accessToken = auth.accessToken;
  const canCallApi = auth.status === "authenticated" && !!accessToken;
  const { tenants, loading: loadingTenants, error: hierarchyError } = useHierarchyContext();

  const [tenantSlugState, setTenantSlugState] = useState<string | null>(() => readStoredTenant());

  const tenantSlug = tenantSlugProp ?? tenantSlugState;

  const [members, setMembers] = useState<TenantMember[]>([]);
  const [invitations, setInvitations] = useState<TenantInvitation[]>([]);

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<TenantRole>("member");

  const [loadingMembers, setLoadingMembers] = useState(false);
  const [loadingInvites, setLoadingInvites] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  /** Invite link from last create (only present once; use when no SMTP or to share manually). */
  const [lastInviteLink, setLastInviteLink] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantSlugProp && tenants.length > 0) {
      setTenantSlugState((prev) => {
        if (prev) return prev;
        const first = tenants[0]?.slug ?? null;
        if (first) writeStoredTenant(first);
        return first;
      });
    }
  }, [tenantSlugProp, tenants]);

  const refreshMembers = async (signal?: AbortSignal) => {
    if (!accessToken || !tenantSlug) return;
    const res = await listTenantMembers(accessToken, tenantSlug, signal);
    setMembers(res);
  };

  const refreshInvitations = async (signal?: AbortSignal) => {
    if (!accessToken || !tenantSlug) return;
    const res = await listTenantInvitations(accessToken, tenantSlug, signal);
    setInvitations(res);
  };

  useEffect(() => {
    if (!canCallApi || !tenantSlug) return;
    const abort = new AbortController();
    setError(null);
    setSuccess(null);
    setLoadingMembers(true);
    refreshMembers(abort.signal)
      .catch((e) => {
        if (abort.signal.aborted) return;
        setError(e instanceof Error ? e.message : "Failed to load members");
      })
      .finally(() => {
        if (abort.signal.aborted) return;
        setLoadingMembers(false);
      });
    return () => abort.abort();
  }, [canCallApi, tenantSlug]);

  useEffect(() => {
    if (!canCallApi || !tenantSlug) return;
    const abort = new AbortController();
    setLoadingInvites(true);
    refreshInvitations(abort.signal)
      .catch(() => {
        // invitations endpoint may not exist yet; keep non-fatal
      })
      .finally(() => {
        if (abort.signal.aborted) return;
        setLoadingInvites(false);
      });
    return () => abort.abort();
  }, [canCallApi, tenantSlug]);

  const membersColumns: Column<TenantMember>[] = useMemo(
    () => [
      {
        id: "user",
        label: "User",
        sortable: true,
        sortValue: (m) => m.user?.email ?? "",
        render: (m) => (
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" noWrap>
              {m.user?.email ?? "-"}
            </Typography>
            <Typography variant="caption" className="om-text-mono" sx={{ opacity: 0.7 }} noWrap>
              {m.user?.id ?? "-"}
            </Typography>
          </Box>
        ),
      },
      {
        id: "role",
        label: "Role",
        sortable: true,
        sortValue: (m) => m.role,
        render: (m) => (
          <Select
            size="small"
            value={String(m.role)}
            disabled={!canCallApi}
            onChange={async (e) => {
              if (!accessToken || !tenantSlug) return;
              const nextRole = String(e.target.value);
              try {
                setError(null);
                setSuccess(null);
                if (!m.user?.id) throw new Error("Invalid member object (missing user id)");
                await updateTenantMemberRole(accessToken, tenantSlug, m.user.id, nextRole);
                await refreshMembers();
                setSuccess("Role updated");
              } catch (err) {
                setError(err instanceof Error ? err.message : "Failed to update role");
              }
            }}
          >
            {ROLES.map((r) => (
              <MenuItem key={r} value={r}>
                {r}
              </MenuItem>
            ))}
            {!ROLES.includes(m.role as TenantRole) ? (
              <MenuItem value={String(m.role)}>{String(m.role)}</MenuItem>
            ) : null}
          </Select>
        ),
      },
      {
        id: "joined",
        label: "Joined",
        sortable: true,
        sortValue: (m) => (m.joined_at ? new Date(m.joined_at) : new Date(0)),
        render: (m) => (
          <Typography variant="body2" sx={{ opacity: 0.8 }} noWrap>
            {m.joined_at ? new Date(m.joined_at).toLocaleString() : "-"}
          </Typography>
        ),
      },
      {
        id: "actions",
        label: <Box sx={{ display: "flex", justifyContent: "flex-end", mx: 1 }}>Actions</Box>,
        render: (m) => (
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              size="small"
              color="error"
              variant="text"
              disabled={!canCallApi}
              onClick={async () => {
                if (!accessToken || !tenantSlug) return;
                try {
                  setError(null);
                  setSuccess(null);
                  if (!m.user?.id) throw new Error("Invalid member object (missing user id)");
                  await removeTenantMember(accessToken, tenantSlug, m.user.id);
                  await refreshMembers();
                  setSuccess("Member removed");
                } catch (err) {
                  setError(err instanceof Error ? err.message : "Failed to remove member");
                }
              }}
            >
              Remove
            </Button>
          </Box>
        ),
      },
    ],
    [accessToken, canCallApi, tenantSlug],
  );

  const invitesColumns: Column<TenantInvitation>[] = useMemo(
    () => [
      {
        id: "email",
        label: "Email",
        sortable: true,
        sortValue: (i) => i.email,
        render: (i) => (
          <Typography variant="body2" noWrap>
            {i.email}
          </Typography>
        ),
      },
      {
        id: "role",
        label: "Role",
        sortable: true,
        sortValue: (i) => i.role ?? "",
        render: (i) => (
          <Typography variant="body2" sx={{ opacity: 0.8 }} noWrap>
            {i.role ?? "-"}
          </Typography>
        ),
      },
      {
        id: "expires",
        label: "Expires",
        sortable: true,
        sortValue: (i) => new Date(i.expires_at),
        render: (i) => (
          <Typography variant="body2" sx={{ opacity: 0.8 }} noWrap>
            {new Date(i.expires_at).toLocaleString()}
          </Typography>
        ),
      },
      {
        id: "actions",
        label: <Box sx={{ display: "flex", justifyContent: "flex-end", mx: 1 }}>Actions</Box>,
        render: (i) => (
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              size="small"
              variant="text"
              disabled={!canCallApi}
              onClick={async () => {
                if (!accessToken || !tenantSlug) return;
                try {
                  setError(null);
                  setSuccess(null);
                  await cancelTenantInvitation(accessToken, tenantSlug, i.id);
                  await refreshInvitations();
                  setSuccess("Invitation cancelled");
                } catch (err) {
                  setError(err instanceof Error ? err.message : "Failed to cancel invitation");
                }
              }}
            >
              Cancel
            </Button>
          </Box>
        ),
      },
    ],
    [accessToken, canCallApi, tenantSlug],
  );

  if (auth.status !== "authenticated") {
    return (
      <Panel title="Tenant members">
        <Alert severity="info">Please log in to manage tenant members.</Alert>
      </Panel>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Panel title="Tenant members" subtitle="Manage roles and invitations">
        <Stack spacing={2}>
          {hierarchyError ? (
            <Alert severity="error">{hierarchyError.message}</Alert>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : null}
          {success ? <Alert severity="success">{success}</Alert> : null}

          {!tenantSlugProp && (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start">
              <Box sx={{ minWidth: 240 }}>
                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                  Tenant
                </Typography>
                <Select
                  fullWidth
                  size="small"
                  value={tenantSlug ?? ""}
                  disabled={loadingTenants || tenants.length === 0 || !!hierarchyError}
                  onChange={(e) => {
                    const slug = String(e.target.value);
                    setTenantSlugState(slug);
                    writeStoredTenant(slug);
                  }}
                >
                  {tenants.map((t) => (
                    <MenuItem key={t.slug} value={t.slug}>
                      {t.name} ({t.slug})
                    </MenuItem>
                  ))}
                </Select>
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Select a tenant to view members and manage invitations.
                </Typography>
              </Box>
            </Stack>
          )}

          {!tenantSlug ? (
            <Alert severity="warning">No tenant selected.</Alert>
          ) : (
            <>
              <Divider />

              <Typography variant="subtitle2">Invite member</Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  label="Email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  size="small"
                  fullWidth
                />
                <Select
                  size="small"
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value as TenantRole)}
                  sx={{ minWidth: 160 }}
                >
                  {ROLES.map((r) => (
                    <MenuItem key={r} value={r}>
                      {r}
                    </MenuItem>
                  ))}
                </Select>
                <Button
                  variant="contained"
                  disabled={!canCallApi || inviteEmail.trim().length === 0}
                  onClick={async () => {
                    if (!accessToken || !tenantSlug) return;
                    try {
                      setError(null);
                      setSuccess(null);
                      setLastInviteLink(null);
                      const res = await inviteTenantMember(accessToken, tenantSlug, {
                        email: inviteEmail.trim(),
                        role: inviteRole,
                      });
                      setInviteEmail("");
                      await refreshInvitations();
                      const inviteLink =
                        res.token != null
                          ? `${window.location.origin}/invitation/${res.token}`
                          : null;
                      setLastInviteLink(inviteLink ?? null);
                      setSuccess(
                        inviteLink
                          ? "Invitation created. Copy the link below to share (email may not be sent if SMTP is not configured)."
                          : "Invitation sent",
                      );
                    } catch (err) {
                      setError(err instanceof Error ? err.message : "Failed to send invitation");
                    }
                  }}
                >
                  Invite
                </Button>
              </Stack>

              {lastInviteLink ? (
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="center">
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: "monospace",
                      fontSize: "0.75rem",
                      wordBreak: "break-all",
                      maxWidth: "100%",
                    }}
                  >
                    {lastInviteLink}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      void navigator.clipboard.writeText(lastInviteLink).then(() => {
                        setSuccess("Invite link copied to clipboard");
                      });
                    }}
                  >
                    Copy invite link
                  </Button>
                </Stack>
              ) : null}
            </>
          )}
        </Stack>
      </Panel>

      {tenantSlug ? (
        <>
          <Panel title="Members" subtitle={`Tenant: ${tenantSlug}`}>
            <CompGrid
              items={members}
              loading={loadingMembers}
              error={null}
              emptyMessage="No members found."
              columns={membersColumns}
              keyExtractor={(m) => `${m.user.id}:${String(m.role)}`}
            />
          </Panel>

          <Panel title="Pending invitations" subtitle="Invitations that haven’t been accepted yet">
            <CompGrid
              items={invitations}
              loading={loadingInvites}
              error={null}
              emptyMessage="No pending invitations (or endpoint not enabled)."
              columns={invitesColumns}
              keyExtractor={(i) => i.id}
            />
          </Panel>
        </>
      ) : null}
    </Box>
  );
}

